// src/components/DetailedDescription.tsx
import React, { useState } from "react";
import { ChevronDown, ChevronUp, FileText } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";

interface DetailedDescriptionWidgetProps {
  detailedDescription?: string;
}

const DetailedDescriptionWidget: React.FC<DetailedDescriptionWidgetProps> = ({ 
  detailedDescription 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Om det inte finns någon detaljerad beskrivning, visa inte widgeten
  if (!detailedDescription || detailedDescription.trim() === "") {
    return null;
  }

  // Dela upp beskrivningen i stycken för bättre läsbarhet
  const paragraphs = detailedDescription
    .split(/\n+/)
    .filter(p => p.trim().length > 0);

  // Hantera växling mellan expanderat och icke-expanderat läge
  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div className="relative">
      {/* Klickbart block för att expandera/kollapsa beskrivningen */}
      <button
        onClick={toggleExpand}
        className={cn(
          "w-full flex items-center justify-between gap-3 p-3 rounded-lg text-left",
          "bg-gray-800/50 hover:bg-gray-700/50 transition-colors mb-2",
          "text-blue-300 font-medium cursor-pointer"
        )}
      >
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4" />
          <span>Detaljerad beskrivning</span>
        </div>
        
        {isExpanded ? (
          <ChevronUp className="h-5 w-5" />
        ) : (
          <ChevronDown className="h-5 w-5" />
        )}
      </button>

      {/* Fullständigt innehåll när expanderad */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="text-gray-300 bg-gray-800/50 rounded-lg p-4 text-sm leading-relaxed"
          >
            {paragraphs.map((paragraph, index) => (
              <p key={index} className={index > 0 ? "mt-4" : ""}>
                {paragraph}
              </p>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DetailedDescriptionWidget;